import { memo, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormBuilderCardWrapper } from '../../../FormBuilderCardWrapper';
import { FormBuilderElement } from '../../../FormBuilderElement';
import {
  useFormBuilderFields,
  useFormBuilderFieldWithError,
} from '../../../hooks';
import { EFormBuilderFieldType, TFormBuilderCheckboxField } from '@/types';

type TProps = {
  field: TFormBuilderCheckboxField;
  index: number;
};

export const FormBuilderCheckbox =
  /**
   * Checkbox field component for form builder with editable label and options
   * @param {TProps} props - Component props
   * @param {TFormBuilderCheckboxField} props.field - Checkbox field data
   * @param {number} props.index - Field index in the form
   * @returns {JSX.Element} Form builder checkbox field component
   */
  memo(({ field, index }: TProps) => {
    const { t } = useTranslation();
    const [active, setActive] = useState(false);
    const onActiveChange = useCallback(
      (value: boolean) => setActive(value),
      [],
    );
    const error =
      useFormBuilderFieldWithError<TFormBuilderCheckboxField>(index);
    const { changeFields, removeField, copyField, moveDownField, moveUpField } =
      useFormBuilderFields(field);

    const changeCheckboxFields = useCallback(
      (updated: Partial<TFormBuilderCheckboxField>) => {
        changeFields({ ...field, ...updated });
      },
      [changeFields, field],
    );

    return (
      <FormBuilderCardWrapper
        variant={EFormBuilderFieldType.CHECKBOX}
        error={!!error}
        active={active}
        onActiveChange={onActiveChange}
        onRemove={removeField}
        onDownMove={moveDownField}
        onUpMove={moveUpField}
        onDuplicate={copyField}
      >
        <FormBuilderElement
          label={
            <FormBuilderElement.Label
              label={field.label}
              onLabelChange={(label) => changeCheckboxFields({ label })}
              placeholder={t('form-builder.your-label-here')}
              required={field.required}
              error={!!error?.label}
              sx={{
                fontSize: '14px',
                fontWeight: '400',
                lineHeight: '20px',
                color: 'var(--colors-gray500)',
                wordBreak: 'break-all',
              }}
            />
          }
          required={field.required}
          onRequiredChange={(required) => changeCheckboxFields({ required })}
        >
          <FormBuilderElement.Checkbox
            label={field.options[0].value}
            onLabelChange={(value) =>
              changeCheckboxFields({
                options: [{ ...field.options[0], value }],
              })
            }
            error={!!error?.options?.[0]}
          />
        </FormBuilderElement>
      </FormBuilderCardWrapper>
    );
  });

FormBuilderCheckbox.displayName = 'FormBuilderCheckbox';
